import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useDeleteIdentificationDocumentMutation, {
  DeleteIdentificationDocumentArgs,
} from 'components/domain/lead/useDeleteIdentificationDocumentMutation';
import { LEAD_GQL_QUERY_BASE } from 'components/domain/lead/useFetchLead';
import { modalIds } from 'components/modal/Modal.constants';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

/**
 * Deleting a guest's government ID is irreversible, so it goes behind a confirmation step.
 */
const useDeleteIdentificationDocumentModal = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { openConfirmModal, closeModal } = useAppModal();

  const { mutateAsync: deleteIdentificationDocument } =
    useDeleteIdentificationDocumentMutation({
      onSuccess: () => {
        notifySuccess(
          t(
            'componentLead.modal.stayDetailsTab.guestInfo.deleteIdentificationDocumentSuccess',
          ),
        );
        closeModal(modalIds.confirm);
        // Refetches the lead so hasIdentificationDocument flips to false and the icons disappear.
        queryClient.invalidateQueries({ queryKey: [LEAD_GQL_QUERY_BASE] });
      },

      onError: (error) => {
        notifyError(
          getErrorMessage({
            apiErrorCode: error.response?.data?.apiErrorCode,
            apiErrorMessage:
              error.response?.data?.apiErrorMessage ??
              t(
                'componentLead.modal.stayDetailsTab.guestInfo.deleteIdentificationDocumentError',
              ),
            baseKey:
              'componentLead.modal.stayDetailsTab.guestInfo.deleteIdentificationDocumentErrorCodes.',
            t,
          }),
        );
      },
    });

  const openDeleteIdentificationDocumentModal = (
    args: DeleteIdentificationDocumentArgs,
  ) => {
    openConfirmModal({
      body: t(
        'componentLead.modal.stayDetailsTab.guestInfo.deleteIdentificationDocumentDescription',
      ),
      confirmColor: 'danger',
      confirmLabel: t('common.delete'),
      onConfirmAsync: async () => {
        await deleteIdentificationDocument(args);
      },
      title: t(
        'componentLead.modal.stayDetailsTab.guestInfo.deleteIdentificationDocument',
      ),
    });
  };

  return {
    openDeleteIdentificationDocumentModal,
  };
};

export default useDeleteIdentificationDocumentModal;
